import React, { useState, useEffect } from 'react';
import AutoCloseSelect from '../../../components/Selects/AutoCloseSelect';
import { useNavigate } from 'react-router-dom';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaHome, FaGlobe, FaEdit, FaTrashAlt, FaChartLine, FaChartBar, FaDollarSign } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { API_BASE } from '../../../services/apiClient';
import {
  PageContainer,
  Header,
  HeaderActions,
  ActionButton,
  AgencyLogo,
  AgencyInfo,
  AgencySelector,
  StatCard,
  Grid,
  Section,
  SectionHeader,
  TabsContainer,
  Tab,
  ChartContainer,

  TeamMember
} from './styles';

const Agency = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedAgency, setSelectedAgency] = useState(() => {
    try { return localStorage.getItem('selected_imobiliaria') || null; } catch { return null; }
  });
  const [agencies, setAgencies] = useState([]);
  const [agencyDetails, setAgencyDetails] = useState(null);
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const resolveLogoUrl = (src) => {
    if (!src) return null;
    if (/^https?:\/\//i.test(src)) return src;
    const base = (API_BASE || '').replace(/\/+$/, '');
    const path = src.startsWith('/') ? src : `/${src}`;
    return `${base}${path}`;
  };

  // Normaliza o agente vindo da API para o formato usado em "Nossa Equipe"
  const mapApiAgent = (a) => ({
    id: a.codigoAGENTE,
    name: a.nomeAGENTE,
    role: a.descricaoAGENTE,
    status: (a.ativoAGENTE === true || a.ativoAGENTE === 1 || a.ativoAGENTE === '1') ? 'active' : 'inactive',
    initials: (a.nomeAGENTE?.split(' ').map(n => n[0]).join('')).substring(0, 2).toUpperCase(),
    properties: 0,
    sales: 0,
    email: a.emailAGENTE,
    phone: a.telefoneAGENTE,
    imobiliariaId: a.codigoIMOBILIARIA || a.imobiliaria || a.imobiliariaId
  });

  // Cache local de rascunhos, compartilhado com a página Agents
  const DRAFT_KEY = 'agents_local_cache';
  const readDrafts = () => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      return Array.isArray(JSON.parse(raw)) ? JSON.parse(raw) : [];
    } catch { return []; }
  };
  const writeDrafts = (arr) => {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(arr || [])); } catch {}
  };

  // Helpers para evitar renderização de objetos como filhos React
  const safeText = (val, fallback = 'N/A') => {
    if (val === null || val === undefined) return fallback;
    if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') return String(val);
    // Se vier objeto/array, tenta extrair algo útil
    if (Array.isArray(val)) return val.length ? String(val.length) : fallback;
    return fallback;
  };

  const safeCount = (val) => {
    if (val === null || val === undefined) return 0;
    if (typeof val === 'number') return val;
    if (Array.isArray(val)) return val.length;
    if (typeof val === 'object') return 1; // evita crash caso backend envie um único objeto
    const n = Number(val);
    return Number.isFinite(n) ? n : 0;
  };

  useEffect(() => {
    const fetchAgencies = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch(`${import.meta.env.VITE_URL_API}/imobiliarias/me`,{
          headers: {
            accept: 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          }
        });
        let data = [];
        if (response.ok) {
          try { 
            const body = await response.json();
            // Aceita tanto array direto quanto objeto com campo `dados`
            data = Array.isArray(body) ? body : (Array.isArray(body?.dados) ? body.dados : []);
          } catch { data = []; }
        }

        const list = Array.isArray(data) ? data : [];
        setAgencies(list);
        let initialId = (localStorage.getItem('selected_imobiliaria'))
          || (list[0]?.codigoIMOBILIARIA?.toString?.());

        // Se não houver ID em localStorage ou não existir na lista carregada,
        // tenta localizar pela dica salva (nome/email) após criação
        const existsInList = (id) => list?.some?.(a => a?.codigoIMOBILIARIA?.toString?.() === id);
        if (!initialId || !existsInList(initialId)) {
          try {
            const hintRaw = localStorage.getItem('last_agency_hint');
            if (hintRaw) {
              const hint = JSON.parse(hintRaw || '{}');
              const match = Array.isArray(list) ? list.find(a => {
                const n = (a.nomeIMOBILIARIA || a.name || '').trim().toLowerCase();
                const e = (a.emailIMOBILIARIA || a.email || '').trim().toLowerCase();
                const hn = (hint.name || '').trim().toLowerCase();
                const he = (hint.email || '').trim().toLowerCase();
                return (hn && n === hn) || (he && e === he);
              }) : null;
              if (match?.codigoIMOBILIARIA) {
                initialId = match.codigoIMOBILIARIA.toString();
              }
              // limpa a dica após usar
              localStorage.removeItem('last_agency_hint');
            }
          } catch {}
        }

        if (initialId) {
          setSelectedAgency(initialId);
          try { localStorage.setItem('selected_imobiliaria', initialId); } catch {}
        }
        setAgencyDetails(list.find(a => a.codigoIMOBILIARIA?.toString?.() === initialId) || list[0]);
      } catch (error) {
        console.error('Error fetching agencies:', error);
        setAgencies([]);
        setAgencyDetails(null);
      }
    };

    fetchAgencies();
  }, []);

  useEffect(() => {
    if (selectedAgency) {
      const agency = agencies.find(a => a.codigoIMOBILIARIA.toString() === selectedAgency);
      setAgencyDetails(agency);
    }
  }, [selectedAgency, agencies]);

  useEffect(() => {
    const fetchAgentsForAgency = async () => {
      if (!selectedAgency) return;
      setAgentsLoading(true);
      const token = localStorage.getItem('auth_token');
      try {
        const url = `${import.meta.env.VITE_URL_API}/agentes/me`;
        const res = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'accept': 'application/json'
          }
        });
        let data = [];
        if (res.ok) {
          const body = await res.json();
          data = Array.isArray(body) ? body : (Array.isArray(body?.dados) ? body.dados : []);
        }

        const apiAgentsRaw = Array.isArray(data) ? data : [];
        // filtra apenas da agência selecionada, se possível detectar
        const filteredRaw = apiAgentsRaw.filter(a =>
          (a?.codigoIMOBILIARIA?.toString?.() === selectedAgency) ||
          (a?.imobiliariaId?.toString?.() === selectedAgency) ||
          (a?.imobiliaria?.toString?.() === selectedAgency)
        );
        const apiAgents = (filteredRaw.length ? filteredRaw : apiAgentsRaw).map(mapApiAgent);

        // Mescla com drafts e remove drafts que já vieram da API
        let drafts = readDrafts();
        const keyOf = (ag) => String(ag.id || '') + '|' + (ag.email || '') + '|' + (ag.name || '');
        const apiKeys = new Set(apiAgents.map(keyOf));
        drafts = drafts.filter(d => !apiKeys.has(keyOf(d)));
        writeDrafts(drafts);

        const byKey = new Map();
        [...apiAgents, ...drafts].forEach(a => {
          const k = keyOf(a);
          if (!byKey.has(k)) byKey.set(k, a);
        });
        const merged = Array.from(byKey.values());

        // Por fim, mantém apenas agentes compatíveis com a agência quando possível
        const finalList = merged.filter(a => !a.imobiliariaId || String(a.imobiliariaId) === String(selectedAgency));
        setAgents(finalList.length ? finalList : merged);
      } catch (err) {
        setAgents([]);
      } finally {
        setAgentsLoading(false);
      }
    };
    fetchAgentsForAgency();
  }, [selectedAgency]);

  const handleDeleteAgency = async () => {
    if (!selectedAgency) return;
    const confirmResult = await Swal.fire({
      title: 'Excluir agência?',
      text: 'Deseja excluir esta agência? Esta ação não poderá ser desfeita.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#c62828',
      cancelButtonColor: '#34574A',
      reverseButtons: true,
    });
    if (!confirmResult.isConfirmed) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const url = `${import.meta.env.VITE_URL_API}/imobiliarias/${selectedAgency}`;
      const res = await fetch(url, {
        method: 'DELETE',
        headers: {
          accept: 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Erro ${res.status}`);
      }

      setAgencies((prev) => prev.filter((a) => a.codigoIMOBILIARIA?.toString?.() !== String(selectedAgency)));
      // Seleciona outra agência se existir
      setSelectedAgency((prev) => {
        const remaining = agencies.filter((a) => a.codigoIMOBILIARIA?.toString?.() !== String(prev));
        const next = remaining[0]?.codigoIMOBILIARIA?.toString?.() || null;
        try { localStorage.setItem('selected_imobiliaria', next || ''); } catch {}
        setAgencyDetails(remaining.find((a) => a.codigoIMOBILIARIA?.toString?.() === next) || null);
        return next;
      });
    } catch (err) {
      Swal.fire('Erro', 'Não foi possível excluir a agência: ' + (err?.message || err), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const team = agents.map(a => {
    const name = a.nomeAGENTE || a.name || '';
    const initials = name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(s => s[0])
      .join('')
      .toUpperCase();

    const statusRaw = a?.ativoAGENTE ?? a?.active ?? a?.status;
    const status = (statusRaw === 'active' || statusRaw === true || statusRaw === 1 || statusRaw === '1') ? 'active' : 'inactive';

    return {
      id: a.codigoAGENTE || a.id || Math.random(),
      name,
      role: a.funcaoAGENTE || a.role || 'Agente',
      status,
      initials: initials || 'AG',
      properties: a.imoveis || a.properties || 0,
      sales: a.vendas || a.sales || 0
    };
  });

  return (
    <PageContainer>
      <AgencySelector>
        <div style={{ minWidth: 260 }}>
          <AutoCloseSelect
            inputId="dashboard-agency-select"
            options={(agencies || []).map(a => ({ value: String(a.codigoIMOBILIARIA), label: a.nomeIMOBILIARIA }))}
            value={(agencies || [])
              .map(a => ({ value: String(a.codigoIMOBILIARIA), label: a.nomeIMOBILIARIA }))
              .find(opt => opt.value === String(selectedAgency)) || null}
            onChange={(opt) => {
              const val = opt?.value || '';
              setSelectedAgency(val);
              try { localStorage.setItem('selected_imobiliaria', val); } catch {}
            }}
            placeholder={(!agencies || agencies.length === 0) ? 'Carregando agências...' : 'Selecione uma agência'}
            isClearable={false}
            styles={{
              control: (base, state) => ({
                ...base,
                borderColor: state.isFocused ? '#34574A' : '#e2e8f0',
                boxShadow: state.isFocused ? '0 0 0 3px rgba(52, 87, 74, 0.1)' : undefined,
                minHeight: 48,
                fontSize: 16
              }),
              option: (base, state) => ({
                ...base,
                background: state.isSelected ? '#34574A' : state.isFocused ? '#f0fff4' : '#fff',
                color: state.isSelected ? '#fff' : '#2d3748',
                fontSize: 16
              })
            }}
            isDisabled={!agencies || agencies.length === 0}
          />
        </div>
        <ActionButton 
          className="primary"
          onClick={() => navigate('/dashboard/new-agency')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14"/>
          </svg>
          Nova Agência
        </ActionButton>
      </AgencySelector>

      {agencyDetails && (
        <>
          <Header>
            <AgencyLogo
              $hasImage={!!resolveLogoUrl(agencyDetails.imagemIMOBILIARIA)}
              style={{
                borderRadius: '50%',
                width: 110,
                height: 110,
                boxShadow: '0 4px 16px #34574A22',
                fontSize: '2.5rem',
                marginRight: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {resolveLogoUrl(agencyDetails.imagemIMOBILIARIA)
                ? <img src={resolveLogoUrl(agencyDetails.imagemIMOBILIARIA)} alt="Logo da agência" />
                : agencyDetails.nomeIMOBILIARIA.slice(0, 2).toUpperCase()}
            </AgencyLogo>
            <AgencyInfo>
              <h1 style={{ fontWeight: 800, fontSize: '2.1rem', color: '#34574A', marginBottom: 6 }}>{agencyDetails.nomeIMOBILIARIA}</h1>
              <p className="description" style={{ color: '#666', fontSize: '1rem', marginBottom: 18 }}>
                {agencyDetails.descricaoIMOBILIARIA || 'Descrição não disponível.'}
              </p>
              <div className="meta">
                <div className="item"><FaMapMarkerAlt style={{ color: '#34574A', marginRight: 6 }} />{agencyDetails.enderecoIMOBILIARIA || 'Endereço não disponível'}</div>
                <div className="item"><FaPhone style={{ color: '#34574A', marginRight: 6 }} />{agencyDetails.telefoneIMOBILIARIA || 'Telefone não disponível'}</div>
                <div className="item"><FaEnvelope style={{ color: '#34574A', marginRight: 6 }} />{agencyDetails.emailIMOBILIARIA || 'Email não disponível'}</div>
              </div>
            </AgencyInfo>
            <HeaderActions>
              <ActionButton 
                className="secondary"
                onClick={() => navigate('/dashboard/edit-agency/' + selectedAgency, { state: { agencyDetails } })}
                style={{ fontWeight: 700, fontSize: '1rem', padding: '12px 28px' }}
              >
                <FaEdit style={{ marginRight: 8, fontSize: 18 }} />
                Editar Agência
              </ActionButton>
              <ActionButton
                className="secondary"
                onClick={handleDeleteAgency}
                disabled={deleting}
                style={{ fontWeight: 700, fontSize: '1rem', padding: '12px 28px', borderColor: '#c62828', color: '#c62828' }}
              >
                <FaTrashAlt style={{ marginRight: 8, fontSize: 18 }} />
                {deleting ? 'Excluindo...' : 'Excluir Agência'}
              </ActionButton>
            </HeaderActions>
          </Header>

          <Grid>
            <StatCard>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                <FaHome style={{ color: '#34574A', fontSize: 28, background: '#e6f4ea', borderRadius: 8, padding: 6 }} />
                <div className="title">Imóveis</div>
              </div>
              <div className="value">{safeCount(agencyDetails.imovelIMOBILIARIA)}</div>
            </StatCard>
            <StatCard>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                <FaPhone style={{ color: '#34574A', fontSize: 28, background: '#e6f4ea', borderRadius: 8, padding: 6 }} />
                <div className="title">Telefone</div>
              </div>
              <div className="value">{safeText(agencyDetails.telefoneIMOBILIARIA)}</div>
            </StatCard>
            <StatCard>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
                <FaGlobe style={{ color: '#34574A', fontSize: 28, background: '#e6f4ea', borderRadius: 8, padding: 6 }} />
                <div className="title">Idioma</div>
              </div>
              <div className="value">{safeText(agencyDetails.idiomaIMOBILIARIA)}</div>
            </StatCard>
          </Grid>

          {false && (
            <Section>
              <SectionHeader>
  <h2><FaChartLine style={{ marginRight: 8, fontSize: 22 }} />Desempenho</h2>
</SectionHeader>
              <TabsContainer>
                <Tab 
                  active={activeTab === 'overview'} 
                  onClick={() => setActiveTab('overview')}
                >
                  Visão Geral
                </Tab>
                <Tab 
                  active={activeTab === 'sales'} 
                  onClick={() => setActiveTab('sales')}
                >
                  Vendas
                </Tab>
                <Tab 
                  active={activeTab === 'visits'} 
                  onClick={() => setActiveTab('visits')}
                >
                  Visitas
                </Tab>
              </TabsContainer>
              <ChartContainer>
                <div className="placeholder">
                  <FaChartBar style={{ fontSize: 54, marginBottom: 12, color: '#bdbdbd' }} />
                  Em breve você verá gráficos incríveis aqui!
                </div>
              </ChartContainer>
            </Section>
          )}

          <Section>
            <SectionHeader>
              <h2>Nossa Equipe</h2>
            </SectionHeader>
            <Grid>
              {team.map(member => (
                <TeamMember key={member.id} $inactive={member.status !== 'active'}>
                  <div className="avatar">{member.initials}</div>
                  <div className="info">
                    <div className="name">
                      {member.name}
                      {member.status !== 'active' && (
                        <span className="status-badge">Inativo</span>
                      )}
                    </div>
                    <div className="role">{member.role}</div>
                    <div className="stats">
                      <div style={{display:'inline-flex',alignItems:'center',gap:6,background:'#22c55e',color:'#fff',borderRadius:16,padding:'2px 12px',fontWeight:600,fontSize:'0.92rem'}}><FaHome /> {safeCount(member.properties)} imóveis</div>
                      <div style={{display:'inline-flex',alignItems:'center',gap:6,background:'#34574A',color:'#fff',borderRadius:16,padding:'2px 12px',fontWeight:600,fontSize:'0.92rem'}}><FaDollarSign /> {safeCount(member.sales)} vendas</div>
                    </div>
                  </div>
                </TeamMember>
              ))}
            </Grid>
          </Section>
        </>
      )}
    </PageContainer>
  );
};
export default Agency;